import { useState } from "react";
import { toast } from "sonner";
import { Blocks, Plus, RotateCcw, X } from "lucide-react";
import type { AgentPluginSummary, Plugin, ProcessInstanceView, ProcessPluginRefView } from "@/types";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePlugins, useAgentPlugins } from "@/api/queries/plugins";
import { useConfigureInstance } from "@/api/mutations/processInstances";
import { CatalogSourceAlert } from "@/components/plugin-catalog/CatalogStatus";

// #320 (UC-2): „Benötigte Plugins" an der Einrichtung.
//
// Der Prozess-Autor GIBT Plugins VOR (Empfehlung, kein Zwang); der Inhaber der Einrichtung
// darf die Liste vollständig ersetzen — weglassen, wegnehmen, reintun. Deshalb zeigt die
// Karte immer die EFFEKTIVE Liste und sagt daneben, woher sie kommt.
//
// Drei Zustände, die man nicht verwechseln darf (der Master liefert sie ausdrücklich):
//  · `spec`     — Vorgabe des Prozesses, nichts überschrieben;
//  · `instance` — eigene Auswahl (auch die LEERE Auswahl ist eine Aussage);
//  · `unknown`  — die Vorgabe war nicht abrufbar. `effectivePlugins` ist dann `null`
//                 (Review L1) — NICHT `[]`, denn „wir wissen es nicht" ist nicht
//                 „es ist keins", und genau das steht dann auch da.

const schluessel = (p: ProcessPluginRefView): string => `${p.marketplaceId}:${p.pluginName}`;

/** Was das Auswahlfeld braucht — Katalog-Sicht und Agent-Sicht liefern beides. */
type Kandidat = Pick<Plugin, "marketplaceId" | "marketplaceName" | "name" | "ref" | "status" | "approved">;

export function RequiredPluginsCard({
  instance,
  agentId,
  darfEdit,
}: {
  instance: ProcessInstanceView;
  /** Der EFFEKTIVE Agent (fixierter aus der Vorgabe ?? gebundener) — `null` = noch keiner. */
  agentId: string | null;
  darfEdit: boolean;
}) {
  // #320 (Review L3): das Auswahlfeld zeigt den Katalog, den DIESER AGENT sehen kann.
  // Marktplätze können agent-scoped sein (#287) — die Plattform-Liste böte dann Plugins an,
  // die an diesem Agenten gar nicht zuweisbar sind, und der Nutzer liefe erst beim
  // Aktivieren in die Wand. Ohne gebundenen Agenten gibt es diese Sicht nicht; dann bleibt
  // die Plattform-Liste plus der Hinweis, dass die verbindliche Prüfung an der
  // Bereitschaft stattfindet.
  //
  // ⚠ Beide Hooks werden IMMER gerufen (Hook-Regel); der jeweils unbenutzte ist entweder
  // deaktiviert (ohne agentId) oder ein react-query-Cache-Treffer.
  // ⚠ Für einen Nicht-Admin liefert `/agents/:id/plugins` nur sein `ownerAvailable`-Subset —
  // enger als das, was der Prozessweg dürfte (dort reicht „freigeschaltet", Ticket-Politik).
  // Das ist bewusst hingenommen: die Plattform-Liste bekäme er mangels `plugins:read`
  // überhaupt nicht, und was hier fehlt, benennt die Bereitschaft als Lücke.
  const agentKatalog = useAgentPlugins(agentId ?? undefined);
  const plattformKatalog = usePlugins();
  const katalog = agentId ? agentKatalog : plattformKatalog;
  const eintraege: Kandidat[] = agentId
    ? ((agentKatalog.data?.plugins ?? []) as AgentPluginSummary[])
    : (plattformKatalog.data?.plugins ?? []);

  const configure = useConfigureInstance();
  const [suche, setSuche] = useState("");
  const [zeigeAuswahl, setZeigeAuswahl] = useState(false);

  // Version-Skew: ein älterer Master kennt das Feld nicht (undefined). Dann gibt es die
  // Vorgabe nicht, und eine Karte mit „keine Plugins" wäre eine Behauptung über etwas, das
  // wir nicht wissen. `null` ist etwas ANDERES: das Feld gibt es, die Antwort lautet
  // „nicht ermittelbar" (Review L1) — die Karte erscheint und sagt genau das.
  if (instance.effectivePlugins === undefined) return null;

  const effektiv = instance.effectivePlugins ?? [];
  const istOverride = instance.plugins != null;
  const quelle = instance.effectivePluginsSource ?? "unknown";
  const gewaehlt = new Set(effektiv.map(schluessel));

  // Auswahl = nur freigeSCHALTETE und im Manifest vorhandene Plugins (dieselbe Regel wie
  // am Agenten: was nicht zugewiesen werden kann, gehört nicht in ein Auswahlfeld).
  // Bereits gewählte fallen raus — sie stehen schon als Chip da.
  const nadel = suche.trim().toLowerCase();
  const kandidaten = eintraege.filter(
    (p) =>
      p.approved &&
      p.status === "available" &&
      !gewaehlt.has(`${p.marketplaceId}:${p.name}`) &&
      (p.name.toLowerCase().includes(nadel) || p.marketplaceName.toLowerCase().includes(nadel)),
  );

  function schreibe(liste: ProcessPluginRefView[] | null, erfolg: string) {
    configure.mutate(
      { id: instance.id, patch: { plugins: liste } },
      {
        onSuccess: (aktualisiert) => {
          toast.success(
            aktualisiert.status === "ready" ? `${erfolg} — Einrichtung ist bereit` : erfolg,
          );
        },
        onError: () => toast.error("Speichern fehlgeschlagen"),
      },
    );
  }

  const gesperrt = !darfEdit || configure.isPending;
  // #320 (Review L7): auf einer nicht abrufbaren Vorgabe darf man nichts aufbauen. Ein
  // „hinzufügen" würde die unvollständige Liste zur EIGENEN Auswahl machen (voller Ersatz!)
  // und dabei still das verlieren, was in der Vorgabe steht und gerade nicht lesbar ist.
  const vorgabeUnklar = quelle === "unknown";

  return (
    <SectionCard
      icon={<Blocks className="h-[18px] w-[18px]" />}
      title="Benötigte Plugins"
      description="Was dieser Prozess an Skills braucht. Vorbelegt aus der Prozess-Vorgabe — hier für diese Einrichtung anpassbar."
      action={
        istOverride && darfEdit ? (
          <button
            className="text-[12.5px] font-semibold text-accent-text hover:underline disabled:opacity-50"
            disabled={gesperrt}
            onClick={() => schreibe(null, "Auf die Prozess-Vorgabe zurückgesetzt")}
            data-testid="instance-plugins-reset"
          >
            <RotateCcw className="mr-1 inline h-3.5 w-3.5" />
            Auf Prozess-Vorgabe zurücksetzen
          </button>
        ) : undefined
      }
      testId="instance-plugins-card"
    >
      <CatalogSourceAlert sources={katalog.data?.sources} />

      <p className="mb-2 text-[12px] text-text3" data-testid="instance-plugins-source">
        {quelle === "instance"
          ? "Eigene Auswahl für diese Einrichtung (die Prozess-Vorgabe ist überschrieben)."
          : quelle === "spec"
            ? "Vorgabe des Prozesses — noch nichts überschrieben."
            : "Die Prozess-Vorgabe ist gerade nicht abrufbar — was hier steht, ist möglicherweise unvollständig."}
      </p>

      {effektiv.length === 0 ? (
        <p className="text-[13px] text-text3">
          {vorgabeUnklar ? "Nicht abrufbar." : "Keine Plugins nötig."}
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2" data-testid="instance-plugins-chips">
          {effektiv.map((p) => (
            <li
              key={schluessel(p)}
              className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5"
              data-testid={`instance-plugin-chip-${p.marketplaceId}-${p.pluginName}`}
            >
              <span className="text-[13px] font-medium text-text">{p.pluginName}</span>
              <span className="mono text-[11px] text-text3">{p.marketplaceId}</span>
              {darfEdit && !vorgabeUnklar && (
                <button
                  className="text-text3 hover:text-danger disabled:opacity-50"
                  disabled={gesperrt}
                  title="Aus dieser Einrichtung entfernen"
                  onClick={() =>
                    schreibe(
                      effektiv.filter((x) => schluessel(x) !== schluessel(p)),
                      `${p.pluginName} entfernt`,
                    )
                  }
                  data-testid={`instance-plugin-remove-${p.marketplaceId}-${p.pluginName}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {darfEdit && (
        <div className="mt-3">
          {vorgabeUnklar ? (
            <p className="text-[12.5px] text-text3" data-testid="instance-plugin-add-blocked">
              Ändern ist erst wieder möglich, wenn die Prozess-Vorgabe lesbar ist — sonst würde
              die unvollständige Liste zur festen Auswahl.
            </p>
          ) : !zeigeAuswahl ? (
            <Button variant="outline" onClick={() => setZeigeAuswahl(true)} data-testid="instance-plugin-add">
              <Plus className="h-4 w-4" />
              Plugin hinzufügen
            </Button>
          ) : (
            <div className="space-y-2 rounded-md border border-border p-2.5">
              <Input
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="Plugin suchen…"
                data-testid="instance-plugin-search"
              />
              {!agentId && (
                <p className="text-[12px] text-text3" data-testid="instance-plugin-picker-hint">
                  Ohne gebundenen Agenten steht hier der Plattform-Katalog — ob ein Plugin am
                  Agenten wirklich verfügbar ist, sagt die Bereitschaft.
                </p>
              )}
              {katalog.isError ? (
                <p className="text-[12.5px] text-danger">Katalog nicht abrufbar.</p>
              ) : kandidaten.length === 0 ? (
                <p className="text-[12.5px] text-text3">
                  Kein freigeschaltetes Plugin passt. Freischalten passiert im Plugin-Katalog.
                </p>
              ) : (
                <ul className="max-h-48 space-y-1 overflow-auto">
                  {kandidaten.map((p) => (
                    <li key={p.ref}>
                      <button
                        className="w-full rounded px-2 py-1 text-left text-[13px] hover:bg-surface-2 disabled:opacity-50"
                        disabled={gesperrt}
                        onClick={() => {
                          setZeigeAuswahl(false);
                          setSuche("");
                          schreibe(
                            [...effektiv, { marketplaceId: p.marketplaceId, pluginName: p.name }],
                            `${p.name} hinzugefügt`,
                          );
                        }}
                        data-testid={`instance-plugin-option-${p.marketplaceId}-${p.name}`}
                      >
                        {p.name}
                        <span className="ml-2 mono text-[11px] text-text3">{p.marketplaceName}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}
